/*
 * aiClerk 团队相册选图弹窗
 *
 * 整体 UI 跟批量编辑添加照片完全 1:1 —— 直接复用从 src/pages/watermark/addPhotos/
 * 完整拷贝过来的 ./TeamPhotoPicker(在 aiClerk 模块下),只在拷贝版里加了几个
 * 调用方扩展点:onConfirm 回调 / excludePhotos 排除已选 / relaxFilter 放宽过滤 /
 * headerTitle 自定义标题。
 *
 * Modal 尺寸跟 AddPhotosModal 一致:92vw / max 1280px / top 24px / zIndex 1100。
 */

import React from 'react'
import { Modal } from 'antd'
import TeamPhotoPicker from './TeamPhotoPicker'
import './TeamPhotoPickerModal.scss'

export interface TeamPhotoPickResult {
  url: string
  name: string
  mediaType: string
  seq: number
  smallURL: string
}

interface TeamPhotoPickerModalProps {
  visible: boolean
  maxCount: number
  /** 标题(默认"团队相册选图") */
  title?: string
  /** 已经被选过的照片,会在 picker 内过滤掉 */
  excludePhotos?: Array<{ seq?: number; smallURL?: string; [key: string]: any }>
  onConfirm: (photos: TeamPhotoPickResult[]) => void
  onCancel: () => void
}

// 从 picker 返回的 plain photo(含 seq/url/smallURL/extension 等) 转成 aiClerk 这边
// 用的 attachment 列表(url/name/mediaType + seq/smallURL 用于排除已选)
function mapPickerPhotosToResult(
  photos: any[],
  maxCount: number
): TeamPhotoPickResult[] {
  const cut = photos.slice(0, maxCount)
  return cut
    .map((p) => {
      const seq = typeof p.seq === 'number' ? p.seq : 0
      const url = p.url || p.largeURL || p.smallURL || ''
      const smallURL = p.smallURL || url
      if (!url) return null
      let name = `team-photo-${seq}.jpg`
      try {
        const u = new URL(url, window.location.origin)
        const last = u.pathname.split('/').pop()
        if (last) name = decodeURIComponent(last.split('?')[0] || last)
      } catch {
        // 兜底用 seq
      }
      return {
        url,
        name,
        mediaType: 'image/jpeg',
        seq,
        smallURL
      } as TeamPhotoPickResult
    })
    .filter((x): x is TeamPhotoPickResult => x !== null)
}

export const TeamPhotoPickerModal: React.FC<TeamPhotoPickerModalProps> = ({
  visible,
  maxCount,
  title = '团队相册选图',
  excludePhotos,
  onConfirm,
  onCancel
}) => {
  const handleInnerConfirm = React.useCallback(
    (photos: any[]) => {
      const result = mapPickerPhotosToResult(photos || [], maxCount)
      if (result.length === 0) return
      onConfirm(result)
    },
    [maxCount, onConfirm]
  )

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      mask={{ closable: false }}
      keyboard={false}
      destroyOnHidden
      wrapClassName="aiClerk-team-picker__wrap"
      zIndex={1100}
      title={null}
      onCancel={onCancel}
    >
      {visible && (
        <TeamPhotoPicker
          inModal
          onClose={onCancel}
          headerTitle={title}
          excludePhotos={excludePhotos}
          relaxFilter
          // aiClerk 硬上限 30 张,防止给 AI 的 context 过大;调用方传更小的就用更小的
          maxCount={Math.max(1, Math.min(maxCount, 30))}
          onConfirm={handleInnerConfirm}
        />
      )}
    </Modal>
  )
}
